import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CredentialPickerScreen.css';

// Full-screen credential picker chrome — takes over the screen with a back button and returns on
// selection. Shared by sync, payment porter and vault so "Add / Change credential" opens the
// identical screen everywhere. The actual picker (and any per-consumer extras like overrides or the
// HConnect-managed option) are passed as children.
//
// RENDERED INTO document.body, NOT WHERE IT IS CALLED FROM.
//
// `position: fixed` is measured against the viewport only while no ancestor has created a containing
// block. A `transform`, `filter`, `perspective`, `will-change` or `contain` anywhere above it — the
// kind of thing added to a card or a board for a hover effect, far from here and with no idea this
// exists — silently re-anchors it to that ancestor. The screen then covers a corner of whatever it was
// opened from and the page shows through beneath it: not a full-screen picker, just a box in the way.
//
// Opened from the pipeline designer, that is exactly what happened. A portal makes it independent of
// every ancestor it will ever be mounted under, which for a component whose whole contract is "takes
// over the screen" is the only version of it that is actually true.
export default function CredentialPickerScreen({ title = 'Connection Credentials', subtitle, onBack, children }) {
    // NOTHING SCROLLS BEHIND IT. With the picker out of the document flow the page underneath keeps its
    // own scrollbar, so a wheel over the picker moved the page it is covering.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, []);

    // Escape closes it, like every other full-screen thing on the site.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onBack(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onBack]);

    return createPortal(
        <div className="cred-screen" role="dialog" aria-modal="true" aria-label={title}>
            <div className="cred-screen-inner">
                <button type="button" className="cred-screen-back" onClick={onBack}>← Back</button>
                <h1 className="cred-screen-title">{title}</h1>
                {subtitle && <p className="cred-screen-subtitle">{subtitle}</p>}
                {children}
            </div>
        </div>,
        document.body,
    );
}
